import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, SidePanel } from '@hue/design-system';
import type { PrepaidBalance } from '@hue/contracts';
import { AppointmentBalanceChoice, prepaidApi } from '../modules/prepaid-balances/index.js';
import { SaleWorkspace } from '../modules/sales/index.js';
import type { PrepaidChoiceProps } from '../modules/scheduling/index.js';
import { getApiError, type Sale } from '../lib/api.js';
import { useCompanyScope } from './company-context.js';
import { newSaleViewDefaults, type SalesView } from './list-views.js';
import { ErrorCard, LoadingCard } from './data-view.js';

/// A composição aproxima a compra da Agenda sem um módulo importar o outro.
/// A venda já concluída nunca é repetida por falha ao consultar o saldo.
export function AgendaPackageChoice(props: PrepaidChoiceProps) {
  const { can, company } = useCompanyScope();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sale, setSale] = useState<Sale>();
  const [purchased, setPurchased] = useState<PrepaidBalance>();
  const [view, setView] = useState<SalesView>({ ...newSaleViewDefaults, kind: 'PACKAGE' });
  const result = useMutation({
    mutationFn: (saleId: string) =>
      prepaidApi.list(props.clientId, {
        search: '',
        referenceId: props.serviceId,
        saleId,
        limit: 100,
        offset: 0,
      }),
  });
  function consult(saleId: string) {
    result.mutate(saleId, {
      onSuccess: (data) => {
        const ready = data.balances.find(
          (b) =>
            b.clientId === props.clientId &&
            b.kind === 'SERVICE' &&
            b.status === 'AVAILABLE' &&
            b.available > 0 &&
            (!props.date ||
              ((!b.expiresOn || props.date <= b.expiresOn) &&
                (!b.releasedOn || props.date >= b.releasedOn))),
        );
        if (!ready) return;
        setPurchased(ready);
        props.onChange(ready.id);
        setSale(undefined);
        setOpen(false);
      },
    });
  }
  return (
    <>
      <AppointmentBalanceChoice
        {...props}
        purchasedBalance={purchased}
        onPurchase={
          can('sales.create') && can('sales.read')
            ? () => {
                setSale(undefined);
                result.reset();
                setView({ ...newSaleViewDefaults, kind: 'PACKAGE' });
                setOpen(true);
              }
            : undefined
        }
      />
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title="Comprar pacote"
        description={`Para ${props.clientName ?? 'o cliente'}. Após a compra, você volta ao agendamento preenchido. Comprar não garante o horário.`}
        className="max-w-[80rem]"
      >
        {sale ? (
          <div className="grid gap-4">
            <p className="font-semibold">Venda {sale.number} registrada.</p>
            {result.isPending ? (
              <LoadingCard label="Consultando a sessão comprada" />
            ) : result.error ? (
              <>
                <ErrorCard message={getApiError(result.error)} />
                <Button onClick={() => consult(sale.id)}>Consultar saldo novamente</Button>
              </>
            ) : (
              <p role="status">
                Ainda não há sessão liberada e válida para este cliente, serviço e data. Confira o
                pagamento e as regras do pacote em Vendas. A compra já está registrada e não será
                repetida.
              </p>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>
              Voltar ao agendamento
            </Button>
          </div>
        ) : (
          <SaleWorkspace
            embedded
            initialClient={{ id: props.clientId, name: props.clientName ?? '' }}
            requiredServiceId={props.serviceId}
            capabilities={{
              canUseCredit: can('sales.payments.manage'),
              cardsEnabled: can('financial_cards.use'),
              commissionsEnabled: company.featureKeys.includes('sales.commissions'),
              cardAllowed: !company.moduleKeys.includes('finance') || can('financial_cards.use'),
            }}
            view={view}
            onView={(patch) =>
              setView((current) => ({
                ...current,
                ...Object.fromEntries(
                  Object.entries(patch).filter(([, value]) => value !== undefined),
                ),
                kind: 'PACKAGE',
                offset: patch.offset ?? 0,
              }))
            }
            onBack={() => setOpen(false)}
            onCompleted={(saved) => {
              setSale(saved);
              consult(saved.id);
              void cache.invalidateQueries({ queryKey: ['company', 'prepaid'] });
            }}
          />
        )}
      </SidePanel>
    </>
  );
}
